import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, CheckCircle2 } from 'lucide-react';
import { Footer } from '../components/Footer';
import { UpsellPurchaseToast } from '../components/UpsellPurchaseToast';
import { useHotmartSalesFunnel } from '../hooks/useHotmartSalesFunnel';
const MOUNT_ID = 'hotmart-sf-inmunidad27';
export function Inmunidad27Page() {
  // Inline Countdown State (5 minutes)
  const [timeLeft, setTimeLeft] = useState(5 * 60);
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  // Hotmart Sales Funnel
  useHotmartSalesFunnel(MOUNT_ID);
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.6
    }
  };
  const scrollToHotmart = (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById('oferta-especial');
    if (el) {
      const yOffset = -100;
      const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-white font-sans selection:bg-[#E8F5E9] selection:text-[#1B3A5C] pt-16 md:pt-20" data-id="element-556">
      {/* 1. FIXED COUNTDOWN TOP BAR */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-[#FF8A65] text-white py-3 px-4 shadow-sm" data-id="element-557">
        <div className="max-w-3xl mx-auto flex items-center justify-center text-center font-medium text-sm md:text-base" data-id="element-558">
          <span data-id="element-559">
            Oportunidad única disponible por:{' '}
            <span className="font-bold tabular-nums ml-1" data-id="element-560">
              {minutes.toString().padStart(2, '0')}:
              {seconds.toString().padStart(2, '0')}
            </span>
          </span>
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-4 pb-20" data-id="element-561">
        {/* 2. SECTION 1 — HEADLINE */}
        <motion.section {...fadeIn} className="text-center py-8 md:py-12" data-id="element-562">
          <h1 className="text-3xl md:text-5xl font-black text-[#1B3A5C] leading-tight mb-6" data-id="element-563">
            NutriPeques te da las recetas... Pero el Intestino de tu peque
            decide si las aprovecha. 🛡️
          </h1>
          <p className="text-lg md:text-xl text-gray-600 italic max-w-2xl mx-auto" data-id="element-564">
            ¿Sabías que si el sistema digestivo está inflamado, tu hijo no
            absorberá los nutrientes de la comida real que acabás de comprar?
          </p>
        </motion.section>

        {/* 3. SECTION 2 — EL PROBLEMA */}
        <motion.section {...fadeIn} className="mb-12" data-id="element-565">
          <div className="text-lg text-gray-700 space-y-6" data-id="element-566">
            <p data-id="element-567">
              Acabás de dar el paso más importante: comprar recetas saludables.
              Pero hay una realidad que la mayoría de los pediatras no te dice:
            </p>

            <div className="bg-[#E8F5E9] border-l-4 border-[#4CAF50] p-6 rounded-r-2xl my-8 shadow-sm" data-id="element-568">
              <p className="text-xl md:text-2xl font-bold text-[#1B3A5C] text-center" data-id="element-569">
                El 70% del sistema inmune vive en el intestino.
              </p>
            </div>

            <p data-id="element-570">
              Si tu hijo entra en el ciclo de:{' '}
              <strong data-id="element-571">
                Jardín → Virus → Antibiótico → Intestino dañado → Más virus
              </strong>
              , las recetas solas no alcanzan.
            </p>
            <p data-id="element-572">Necesitás blindar las defensas desde la raíz.</p>
          </div>
        </motion.section>

        {/* 4. SECTION 3 — PRESENTACIÓN DEL PRODUCTO */}
        <motion.section {...fadeIn} className="text-center mb-16" data-id="element-573">
          <img src="https://cdn.magicpatterns.com/uploads/kVAFGXF17eWSXNfAqqhSxo/libroinmunidadtotal.png" alt="Inmunidad Total" className="w-full max-w-md mx-auto rounded-xl shadow-2xl mb-8 transform hover:scale-[1.02] transition-transform duration-500" data-id="element-574" />
          <h2 className="text-xl md:text-2xl font-bold text-[#1B3A5C] max-w-2xl mx-auto" data-id="element-575">
            El Protocolo Avanzado de Salud Intestinal para Blindar las Defensas
            de tu Peque — Sin Medicamentos.
          </h2>
        </motion.section>

        {/* 5. SECTION 3.1 — PRIMERA OPORTUNIDAD */}
        <motion.section id="oferta-especial" {...fadeIn} className="bg-[#E8F5E9] rounded-[2rem] p-8 md:p-12 shadow-lg border border-[#C8E6C9] mb-16" data-id="element-576">
          <div className="text-center mb-8" data-id="element-577">
            <div className="inline-block bg-white px-6 py-2 rounded-full font-bold text-gray-600 mb-4 shadow-sm" data-id="element-578">
              Valor Regular:{' '}
              <span className="line-through decoration-red-400 decoration-2" data-id="element-579">
                $99.00 USD
              </span>{' '}
              🚩
            </div>
            <h3 className="text-5xl md:text-6xl font-black text-[#1B3A5C] mb-6" data-id="element-580">
              Solo $27.00 USD
            </h3>
            <p className="text-gray-700 italic font-medium" data-id="element-581">
              Esta oferta es exclusiva para clientes nuevos de NutriPeques. Si
              cerrás esta página, no podrás volver a conseguirla a este precio.
            </p>
          </div>

          {/* HOTMART WIDGET CONTAINER */}
          <div id={MOUNT_ID} className="min-h-[100px] flex items-center justify-center mb-8 relative z-10" data-id="element-582">
            {/* Widget mounts here */}
          </div>

          <div className="flex items-start gap-4 bg-white p-6 rounded-2xl shadow-sm" data-id="element-583">
            <Shield className="w-8 h-8 text-[#4CAF50] shrink-0 mt-1" data-id="element-584" />
            <p className="text-sm text-gray-600 leading-relaxed" data-id="element-585">
              <strong data-id="element-586">Garantía de satisfacción de 30 días</strong> de devolución
              del dinero si sientes que no es para ti o no te aportó valor. Pago
              100% seguro procesado por Hotmart.
            </p>
          </div>
        </motion.section>

        {/* 6. SECTION 4 — BENEFICIOS */}
        <motion.section {...fadeIn} className="mb-16" data-id="element-587">
          <h3 className="text-2xl md:text-3xl font-black text-[#1B3A5C] text-center mb-8" data-id="element-588">
            Lo que vas a lograr:
          </h3>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8" data-id="element-589">
            <ul className="space-y-6" data-id="element-590">
              <li className="flex items-start gap-4" data-id="element-591">
                <CheckCircle2 className="w-6 h-6 text-[#4CAF50] shrink-0 mt-0.5" data-id="element-592" />
                <p className="text-gray-700 text-lg" data-id="element-593">
                  <strong className="text-[#1B3A5C]" data-id="element-594">Cortar el ciclo:</strong>{' '}
                  Cómo salir de la rueda de enfermedades repetidas del jardín.
                </p>
              </li>
              <li className="flex items-start gap-4" data-id="element-595">
                <CheckCircle2 className="w-6 h-6 text-[#4CAF50] shrink-0 mt-0.5" data-id="element-596" />
                <p className="text-gray-700 text-lg" data-id="element-597">
                  <strong className="text-[#1B3A5C]" data-id="element-598">Protocolo SOS:</strong> Qué
                  hacer exactamente en las primeras 2 horas de síntomas para
                  evitar la farmacia.
                </p>
              </li>
              <li className="flex items-start gap-4" data-id="element-599">
                <CheckCircle2 className="w-6 h-6 text-[#4CAF50] shrink-0 mt-0.5" data-id="element-600" />
                <p className="text-gray-700 text-lg" data-id="element-601">
                  <strong className="text-[#1B3A5C]" data-id="element-602">
                    Guía de Suplementación:
                  </strong>{' '}
                  Qué pedirle a tu pediatra (Vitamina D, Zinc y Omega 3 con
                  dosis reales).
                </p>
              </li>
              <li className="flex items-start gap-4" data-id="element-603">
                <CheckCircle2 className="w-6 h-6 text-[#4CAF50] shrink-0 mt-0.5" data-id="element-604" />
                <p className="text-gray-700 text-lg" data-id="element-605">
                  <strong className="text-[#1B3A5C]" data-id="element-606">
                    15 Recetas Médicas Comestibles:
                  </strong>{' '}
                  Sopas, caldos y shots que saben rico y curan rápido.
                </p>
              </li>
              <li className="flex items-start gap-4" data-id="element-607">
                <CheckCircle2 className="w-6 h-6 text-[#4CAF50] shrink-0 mt-0.5" data-id="element-608" />
                <p className="text-gray-700 text-lg" data-id="element-609">
                  <strong className="text-[#1B3A5C]" data-id="element-610">Paz Mental:</strong> Saber
                  que su cuerpo tiene las herramientas para defenderse solo.
                </p>
              </li>
            </ul>
          </div>
        </motion.section>

        {/* 7. SECTION 5 — LA OFERTA */}
        <motion.section {...fadeIn} className="bg-[#E8F5E9] rounded-3xl p-8 text-center shadow-sm border border-[#C8E6C9] mb-12" data-id="element-611">
          <div className="inline-block bg-white px-4 py-1 rounded-full font-bold text-gray-600 mb-3 text-sm shadow-sm" data-id="element-612">
            Valor Regular:{' '}
            <span className="line-through decoration-red-400" data-id="element-613">$99.00 USD</span>{' '}
            🚩
          </div>
          <h3 className="text-4xl font-black text-[#1B3A5C] mb-4" data-id="element-614">
            Solo $27.00 USD
          </h3>
          <p className="text-gray-700 italic text-sm md:text-base" data-id="element-615">
            Esta oferta es exclusiva para clientes nuevos de NutriPeques. Si
            cerrás esta página, no podrás volver a conseguirla a este precio.
          </p>
        </motion.section>

        {/* 8. SECTION 6 — BOTÓN DE ACCIÓN */}
        <motion.section {...fadeIn} className="text-center mb-16" data-id="element-616">
          <button onClick={scrollToHotmart} className="w-full md:w-auto inline-block bg-[#4CAF50] hover:bg-[#388E3C] text-white font-black text-xl md:text-2xl px-8 py-5 rounded-2xl shadow-[0_8px_0_#2E7D32] hover:shadow-[0_4px_0_#2E7D32] hover:translate-y-1 transition-all" data-id="element-617">
            SÍ, QUIERO INMUNIDAD TOTAL POR $27 USD
          </button>
          <p className="text-gray-500 font-medium mt-6" data-id="element-618">
            Acceso inmediato. Un solo pago. Sin suscripciones.
          </p>
        </motion.section>

        {/* 9. SECTION 7 — GARANTÍA Y SEGURIDAD */}
        <motion.section {...fadeIn} className="text-center max-w-xl mx-auto" data-id="element-619">
          <div className="flex flex-col items-center justify-center gap-4 mb-8" data-id="element-620">
            <Shield className="w-12 h-12 text-[#1B3A5C]" data-id="element-621" />
            <p className="text-sm text-gray-600" data-id="element-622">
              <strong data-id="element-623">Garantía de satisfacción de 30 días</strong> de devolución
              del dinero si sientes que no es para ti o no te aportó valor. Pago
              100% seguro procesado por Hotmart.
            </p>
          </div>

          <div className="space-y-3" data-id="element-624">
            <a href="/thankyou1" className="block text-gray-500 hover:text-gray-800 underline transition-colors text-sm md:text-base" data-id="element-625">
              ¿No querés sumar Inmunidad Total hoy? → Continuar sin este
              protocolo.
            </a>
            <p className="text-xs text-gray-400" data-id="element-626">
              (Esta oferta no estará disponible después de esta página.)
            </p>
          </div>
        </motion.section>
      </main>

      <Footer data-id="element-627" />
      <UpsellPurchaseToast data-id="element-628" />
    </div>;
}